import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion, useMotionValue, useTransform, useSpring, animate, useInView } from "framer-motion";
import {
  ArrowRight, Recycle, Sofa, ShoppingCart, Trash2, ShieldCheck, Star,
  Truck, MessageSquare, CreditCard, CheckCircle2,
  Users, TrendingUp, Gift, Zap, Quote, Lock, Camera, Scale,
} from "lucide-react";
import { CATEGORIES } from "@/lib/constants";

const STEPS = [
  { icon: <MessageSquare className="w-5 h-5" />, title: "Opret et job", text: "Beskriv hvad der skal hentes, upload foto og sæt et budget.", color: "from-blue-100 to-blue-50 text-blue-700" },
  { icon: <Users className="w-5 h-5" />, title: "Modtag bud", text: "Lokale chauffører byder på jobbet inden for minutter.", color: "from-amber-100 to-amber-50 text-amber-700" },
  { icon: <CreditCard className="w-5 h-5" />, title: "Betal sikkert", text: "Pengene holdes i depot via Stripe indtil jobbet er færdigt.", color: "from-primary/15 to-primary/5 text-primary" },
  { icon: <CheckCircle2 className="w-5 h-5" />, title: "Godkend & færdig", text: "Du godkender. Chaufføren får udbetalt 80% direkte.", color: "from-emerald-100 to-emerald-50 text-emerald-700" },
];

const TESTIMONIALS = [
  { name: "Anna H.", city: "København", text: "Min gamle sofa blev hentet samme dag. Super nemt og chaufføren var venlig.", rating: 5, avatar: "AH" },
  { name: "Mikkel S.", city: "Aarhus", text: "Sparede mig en hel weekend. Bedre end at leje trailer selv.", rating: 5, avatar: "MS" },
  { name: "Lars D.", city: "Odense", text: "Som chauffør tjener jeg ekstra ved siden af. Platformen er nem at bruge.", rating: 5, avatar: "LD" },
];

const reveal = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 0.61, 0.36, 1] } },
};
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

const PAYMENT_STEPS = [
  { icon: <CreditCard />, color: "bg-primary/10 text-primary", title: "1. Du accepterer et bud og betaler", text: "Pengene trækkes på dit kort, men de udbetales IKKE til chaufføren endnu. De opbevares sikkert hos Grabber." },
  { icon: <Lock />, color: "bg-amber-50 text-amber-700", title: "2. Pengene står parkeret sikkert", text: "Chaufføren kan se, at jobbet er betalt, og kan trygt køre ud til dig, men kan ikke røre pengene." },
  { icon: <Camera />, color: "bg-blue-50 text-blue-700", title: "3. Chaufføren udfører jobbet med fotobevis", text: "Chaufføren skal uploade billeder både ved start og ved aflevering som dokumentation." },
  { icon: <CheckCircle2 />, color: "bg-emerald-50 text-emerald-700", title: "4. DU godkender, først nu udbetales pengene", text: "Når du har set, at jobbet er udført korrekt, trykker du 'Godkend'. Først dér frigives betalingen til chaufføren." },
  { icon: <Scale />, color: "bg-rose-50 text-rose-700", title: "5. Problemer? Du kan åbne en tvist", text: "Er noget gået galt, åbner du en tvist, før du godkender, så kan pengene refunderes til dig." },
];

const CargoBike = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-label="Ladcykel">
    <circle cx="4.3" cy="17.3" r="2.9" />
    <circle cx="20.2" cy="17.6" r="2.4" />
    <rect x="8" y="12.3" width="7.5" height="5" rx="0.6" />
    <path d="M7.2 17.3 H8" />
    <path d="M2.8 9.3 h3.2" />
    <path d="M4.4 9.3 v5.1" />
    <path d="M4.3 17.3 8 14.8" />
    <path d="M16.3 8.7 h2.6" />
    <path d="M17.5 8.7 v3.6" />
    <path d="M17.5 12.3 20.2 15.2" />
  </svg>
);

export default function Home() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, []);
  return (
    <div className="overflow-x-hidden">
      <HeroSection />

      {/* Driving truck divider */}
      <DrivingTruckBar />

      {/* Marquee */}
      <section className="border-y border-border bg-muted/30 overflow-hidden group">
        <div className="py-4 flex animate-marquee whitespace-nowrap group-hover:[animation-play-state:paused]">
          {[...Array(2)].flatMap((_, i) =>
            ["Sofaer", "Køleskabe", "Vaskemaskiner", "Affald", "Møbler", "Reol", "Senge", "Haveaffald", "Småflytning", "Hvidevarer"].map((w, j) => (
              <span key={`${i}-${j}`} className="mx-6 text-sm text-muted-foreground flex items-center gap-3">
                {j % 2 === 0 ? <Truck className="w-4 h-4 text-primary" /> : <CargoBike className="w-5 h-5 text-primary" />} {w}
              </span>
            ))
          )}
        </div>
      </section>

      <LiveStats />

      {/* Why */}
      <motion.section
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger}
        className="py-20 max-w-6xl mx-auto px-4 sm:px-6"
      >
        <motion.div variants={reveal} className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Hvorfor Grabber</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Bedre end <span className="underline-stroke">trailer-leje</span> og opslag.</h2>
          <p className="text-muted-foreground">Den nemmeste måde at få hentet ting på i Danmark</p>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-4">
          <FeatureCard icon={<Zap />} title="Hurtig matchning" text="Bud inden for minutter fra lokale chauffører." accent="from-amber-200/50" />
          <FeatureCard icon={<TrendingUp />} title="Bedste pris" text="Chauffører konkurrerer om dit job for den bedste pris." accent="from-primary/20" />
          <FeatureCard icon={<ShieldCheck />} title="100% sikkert" text="Pengene holdes i depot indtil jobbet er færdigt." accent="from-blue-200/50" />
        </div>
      </motion.section>

      {/* How it works */}
      <section className="py-20 bg-muted/30 relative overflow-hidden">
        <div className="absolute top-10 right-1/4 w-80 h-80 bg-primary/8 rounded-full blur-3xl pointer-events-none animate-float" />
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
          className="max-w-6xl mx-auto px-4 sm:px-6 relative"
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Sådan virker det</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Fra opslag til udført på <span className="text-primary">24 timer</span></h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={i} variants={reveal}>
                <TiltCard>
                  <Card className="p-6 lift relative overflow-hidden h-full">
                    <div className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-30 pointer-events-none`} />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-4">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center`}>
                          {s.icon}
                        </div>
                        <span className="serif-it text-3xl text-muted-foreground/40">{i + 1}</span>
                      </div>
                      <h3 className="font-semibold mb-1.5">{s.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                    </div>
                  </Card>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Payment security / escrow flow */}
      <section className="py-20 relative overflow-hidden" id="tryg-betaling">
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl pointer-events-none animate-float" />
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
          className="max-w-4xl mx-auto px-4 sm:px-6 relative"
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Tryg betaling</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Dine penge er <span className="serif-it text-primary">sikret</span> hele vejen</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Chaufføren får først sine penge, når du har godkendt det udførte job. Sådan fungerer det:</p>
          </motion.div>
          <div className="space-y-0" data-testid="payment-flow-steps">
            {PAYMENT_STEPS.map((s, i) => (
              <motion.div key={i} variants={reveal} className="flex gap-4 sm:gap-6">
                <div className="flex flex-col items-center">
                  <div className={`w-11 h-11 rounded-2xl ${s.color} flex items-center justify-center shrink-0 [&>svg]:w-5 [&>svg]:h-5`}>
                    {s.icon}
                  </div>
                  {i < PAYMENT_STEPS.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
                </div>
                <div className={i < PAYMENT_STEPS.length - 1 ? "pb-8" : ""}>
                  <p className="font-semibold">{s.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div variants={reveal} className="mt-10 p-4 rounded-2xl bg-primary/5 border border-primary/15 flex items-center gap-3 max-w-xl mx-auto">
            <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
            <p className="text-sm text-muted-foreground">Alle betalinger håndteres sikkert af <span className="font-semibold text-foreground">Stripe</span>, samme betalingsplatform som anvendes af bl.a. DBA og Trendsales.</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Categories with parallax tilt */}
      <motion.section
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
        className="py-20 max-w-6xl mx-auto px-4 sm:px-6"
      >
        <motion.div variants={reveal} className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">Kategorier</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Hvad kan du få hentet?</h2>
          <p className="text-muted-foreground">Næsten alt, fra enkelte møbler til hele lejligheder</p>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {CATEGORIES.map((c, i) => (
            <motion.div key={c.value} variants={reveal}>
              <Link to={`/jobs?category=${c.value}`}>
                <TiltCard intensity={8}>
                  <Card className="p-5 text-center lift cursor-pointer h-full group relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <div className="relative">
                      <div className="text-4xl mb-2 inline-block group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-500">{c.emoji}</div>
                      <p className="text-sm font-medium">{c.label}</p>
                    </div>
                  </Card>
                </TiltCard>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Testimonials */}
      <section className="py-20 bg-muted/30 relative overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/8 blob" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-amber-200/30 blob" style={{ animationDelay: "4s" }} />
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={stagger}
          className="max-w-6xl mx-auto px-4 sm:px-6 relative"
        >
          <motion.h2 variants={reveal} className="text-3xl sm:text-4xl font-bold text-center mb-12">Det <span className="serif-it">siger</span> vores brugere</motion.h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} variants={reveal}>
                <TiltCard>
                  <Card className="p-6 lift h-full relative overflow-hidden">
                    <Quote className="absolute top-3 right-3 w-12 h-12 text-primary/10" strokeWidth={1.5} />
                    <div className="relative">
                      <div className="flex gap-0.5 mb-3">
                        {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                      </div>
                      <p className="text-sm leading-relaxed mb-4 text-foreground/90">"{t.text}"</p>
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center text-xs font-bold">{t.avatar}</div>
                        <div>
                          <p className="font-semibold text-xs">{t.name}</p>
                          <p className="text-muted-foreground text-xs">{t.city}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      <BecomeDriverCTA />

      {/* Final CTA */}
      <section className="py-16 bg-foreground text-background relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-primary/30 blob" />
        <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-amber-300/15 blob" style={{ animationDelay: "3s" }} />
        <Sparkles count={18} />
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative z-10"
        >
          <h2 className="text-3xl sm:text-5xl font-bold mb-3 tracking-tighter">Klar til at <span className="serif-it">komme i gang</span>?</h2>
          <p className="text-background/70 mb-6">Opret dit første job gratis, det tager kun 2 minutter.</p>
          <Link to="/post">
            <MagneticButton>
              <Button size="lg" variant="secondary" className="rounded-xl px-8 gap-2 h-12 font-semibold shine-hover">
                Opret et job nu <ArrowRight className="w-4 h-4" />
              </Button>
            </MagneticButton>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

// ============= HERO with aurora + spotlight + floating decorations =============
function HeroSection() {
  const containerRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 20 });
  const sy = useSpring(my, { stiffness: 80, damping: 20 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      mx.set(e.clientX - rect.left - 240);
      my.set(e.clientY - rect.top - 240);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section ref={containerRef} className="relative pt-14 sm:pt-20 pb-20 overflow-hidden grain-bg">
      {/* Aurora gradient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="aurora-1 absolute top-0 left-1/3 w-[600px] h-[400px] bg-primary/20 blur-[80px] rounded-full opacity-60" />
        <div className="aurora-2 absolute top-20 right-1/4 w-[500px] h-[400px] bg-amber-300/30 blur-[100px] rounded-full opacity-50" />
        <div className="aurora-3 absolute bottom-0 left-1/4 w-[500px] h-[300px] bg-blue-200/30 blur-[90px] rounded-full opacity-40" />
      </div>

      <motion.div style={{ x: sx, y: sy }} className="spotlight hidden md:block" />

      {/* Floating little icons in background */}
      <FloatingDeco />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="flex justify-center gap-3 sm:gap-4 mb-8"
        >
          <IconTile color="bg-primary/10 text-primary" delay={0}><Recycle className="w-5 h-5" /></IconTile>
          <IconTile color="bg-amber-100 text-amber-700" delay={0.1}><Sofa className="w-5 h-5" /></IconTile>
          <IconTile color="bg-blue-100 text-blue-700" delay={0.2}><ShoppingCart className="w-5 h-5" /></IconTile>
          <IconTile color="bg-rose-100 text-rose-700" delay={0.3}><Trash2 className="w-5 h-5" /></IconTile>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-5 tracking-wide uppercase"
        >
          <span className="relative w-1.5 h-1.5 rounded-full bg-primary text-primary pulse-ring" />
          Aktiv i hele Danmark
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter mb-4 leading-[1.02]"
        >
          Få hentet <span className="text-primary serif-it font-normal">alt</span> det gamle <br />
          <span className="underline-stroke">hurtigt og nemt</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
          className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed"
        >
          Post et job, lokale chauffører byder, og dine møbler og affald bliver hentet, sikkert, billigt og bæredygtigt.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link to="/post" data-testid="hero-post-cta">
            <MagneticButton>
              <Button size="lg" className="rounded-xl px-8 gap-2 font-semibold shadow-lg shadow-primary/30 h-12 shine-hover">
                Opret et job <ArrowRight className="w-4 h-4" />
              </Button>
            </MagneticButton>
          </Link>
          <Link to="/jobs" data-testid="hero-browse-cta">
            <MagneticButton>
              <Button variant="outline" size="lg" className="rounded-xl px-8 font-medium h-12 border-foreground/15">
                Se aktuelle jobs
              </Button>
            </MagneticButton>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground"
        >
          <a href="#tryg-betaling" className="flex items-center gap-1.5 hover:text-primary transition-colors cursor-pointer" data-testid="hero-trust-payment"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> Sikker betaling via Stripe <span className="underline underline-offset-2">– sådan virker det</span></a>
          <span className="flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500" /> Verificerede chauffører</span>
          <span className="flex items-center gap-1.5"><Recycle className="w-3.5 h-3.5 text-primary" /> Bæredygtig bortskaffelse</span>
        </motion.div>
      </div>
    </section>
  );
}

// Live stats with count-up
function LiveStats() {
  return (
    <section className="py-12 max-w-5xl mx-auto px-4 sm:px-6">
      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
        className="grid grid-cols-3 gap-3 sm:gap-6"
      >
        <CounterStat to={12483} suffix="+" label="Jobs udført" />
        <CounterStat to={98} suffix="%" label="Tilfredshed" />
        <CounterStat to={47} suffix=" min" label="Gns. respons" />
      </motion.div>
    </section>
  );
}

function CounterStat({ to, suffix, label }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 0.61, 0.36, 1],
      onUpdate: (v) => setVal(Math.floor(v)),
    });
    return () => controls.stop();
  }, [isInView, to]);

  return (
    <motion.div ref={ref} variants={reveal} className="text-center">
      <p className="text-3xl sm:text-5xl font-bold tracking-tighter text-primary tabular-nums">{val.toLocaleString("da-DK")}{suffix}</p>
      <p className="text-xs sm:text-sm text-muted-foreground mt-1">{label}</p>
    </motion.div>
  );
}

// Driving truck bar between sections
function DrivingTruckBar() {
  return (
    <div className="relative h-12 overflow-hidden bg-background border-y border-border/40">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>
      <motion.div
        initial={{ x: "-10%" }}
        animate={{ x: "110%" }}
        transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        className="absolute top-1/2 -translate-y-1/2"
      >
        <div className="flex items-center gap-2 bg-background px-3">
          <Truck className="w-6 h-6 text-primary" strokeWidth={1.8} />
          <div className="flex gap-1">
            <span className="w-1 h-1 rounded-full bg-primary/50 animate-pulse" />
            <span className="w-1 h-1 rounded-full bg-primary/30 animate-pulse" style={{ animationDelay: "0.2s" }} />
            <span className="w-1 h-1 rounded-full bg-primary/15 animate-pulse" style={{ animationDelay: "0.4s" }} />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// 3D Tilt wrapper
function TiltCard({ children, intensity = 12 }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useTransform(my, [0, 1], [intensity, -intensity]);
  const rotateY = useTransform(mx, [0, 1], [-intensity, intensity]);

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
      transition={{ type: "spring", stiffness: 200, damping: 30 }}
      className="h-full"
    >
      {children}
    </motion.div>
  );
}

// Magnetic button - slightly follows cursor
function MagneticButton({ children }) {
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 150, damping: 15 });
  const y = useSpring(0, { stiffness: 150, damping: 15 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    x.set(dx * 0.2);
    y.set(dy * 0.2);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ x, y }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

// Decorative floating icons in hero background
function FloatingDeco() {
  const items = [
    { Icon: Truck, x: "8%", y: "20%", size: 28, delay: 0, color: "text-primary/15" },
    { Icon: Sofa, x: "85%", y: "30%", size: 32, delay: 1.5, color: "text-amber-700/15" },
    { Icon: Recycle, x: "12%", y: "70%", size: 24, delay: 0.5, color: "text-primary/15" },
    { Icon: ShoppingCart, x: "82%", y: "65%", size: 26, delay: 2, color: "text-blue-700/15" },
  ];
  return (
    <>
      {items.map(({ Icon, x, y, size, delay, color }, i) => (
        <motion.div
          key={i}
          className={`absolute ${color} hidden md:block`}
          style={{ left: x, top: y }}
          animate={{ y: [0, -15, 0], rotate: [0, 4, -4, 0] }}
          transition={{ duration: 6 + i, repeat: Infinity, delay, ease: "easeInOut" }}
        >
          <Icon style={{ width: size, height: size }} strokeWidth={1.2} />
        </motion.div>
      ))}
    </>
  );
}

// Sparkle particles
function Sparkles({ count = 12 }) {
  const sparks = Array.from({ length: count }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 2,
    size: 2 + Math.random() * 3,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {sparks.map((s, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-amber-300/70"
          style={{ left: s.left, top: s.top, width: s.size, height: s.size }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.4, 0] }}
          transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

function ReferralBanner() {
  return (
    <section className="py-20 max-w-5xl mx-auto px-4 sm:px-6">
      <TiltCard intensity={5}>
        <Card className="relative overflow-hidden border-primary/20 lift">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-primary/20 blob" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-amber-200/40 blob" style={{ animationDelay: "1.5s" }} />
          <Sparkles count={10} />
          <div className="relative grid md:grid-cols-2 gap-8 p-8 sm:p-12 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full mb-4">
                <Gift className="w-3 h-3" /> Inviter en ven
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">
                Få <span className="text-primary serif-it">50 kr.</span>, begge.
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Inviter en ven med din unikke kode. Når de fuldfører deres første job får I begge 50 kr. som rabat på næste betaling.
              </p>
              <Link to="/register">
                <MagneticButton>
                  <Button size="lg" className="rounded-xl gap-2 h-12 px-6 shine-hover">Kom i gang <ArrowRight className="w-4 h-4" /></Button>
                </MagneticButton>
              </Link>
            </div>
            <div className="relative h-56 hidden md:block">
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: [-2, 2, -2], y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="bg-background border-2 border-dashed border-primary/40 rounded-2xl px-8 py-6 shadow-xl shadow-primary/10"
                >
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Din kode</p>
                  <p className="font-mono text-3xl font-bold tracking-[0.25em] text-primary">GRABBER</p>
                </motion.div>
              </div>
            </div>
          </div>
        </Card>
      </TiltCard>
    </section>
  );
}

function BecomeDriverCTA() {
  return (
    <section className="py-20 max-w-5xl mx-auto px-4 sm:px-6">
      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-amber-50/40 lift relative">
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-primary/15 blob" style={{ animationDelay: "2s" }} />
        <div className="grid md:grid-cols-2 gap-8 p-8 sm:p-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full mb-4">
              <Truck className="w-3 h-3" /> Bliv chauffør
            </div>
            <h2 className="text-3xl font-bold mb-3 tracking-tight">Tjen penge som <span className="text-primary">Grabber</span></h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Har du en varevogn eller pickup? Tag jobs i din egen tid og tjen op til 80% af jobbets værdi udbetalt direkte til din bankkonto.
            </p>
            <div className="space-y-2 mb-6">
              {["80% udbetaling, kun 20% kommission", "Du bestemmer selv hvilke jobs du tager", "Hurtig udbetaling efter hvert færdigt job"].map(t => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-2 text-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> {t}
                </motion.div>
              ))}
            </div>
            <Link to="/register">
              <MagneticButton>
                <Button size="lg" className="rounded-xl gap-2 h-12 px-6 shine-hover">Bliv chauffør gratis <ArrowRight className="w-4 h-4" /></Button>
              </MagneticButton>
            </Link>
          </div>
          <div className="relative h-64 hidden md:block">
            <motion.div
              animate={{ x: [-30, 30, -30] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="relative">
                <div className="w-48 h-48 rounded-full bg-primary/10 blur-2xl absolute inset-0" />
                <Truck className="w-32 h-32 text-primary relative" strokeWidth={1} />
              </div>
            </motion.div>
            {/* Road */}
            <div className="absolute bottom-12 inset-x-0 flex items-center gap-2">
              <div className="flex-1 border-t-2 border-dashed border-primary/20" />
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
}

function IconTile({ color, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, delay, type: "spring", stiffness: 200 }}
      whileHover={{ y: -6, rotate: -6, scale: 1.08 }}
      className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color} shadow-sm cursor-pointer`}
    >
      {children}
    </motion.div>
  );
}

function FeatureCard({ icon, title, text, accent }) {
  return (
    <motion.div variants={reveal}>
      <TiltCard>
        <Card className="p-6 lift relative overflow-hidden h-full">
          <div className={`absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br ${accent} rounded-full blur-2xl pointer-events-none`} />
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3 [&>svg]:w-5 [&>svg]:h-5">{icon}</div>
            <h3 className="font-semibold mb-1.5">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
          </div>
        </Card>
      </TiltCard>
    </motion.div>
  );
}
